import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./service.module.css";

import image2 from "./sliderImages/design-engineering.avif";
import image3 from "./sliderImages/image-of-female-hand-powering-on-automobile-showing-interior-steering-wheel-center-console-584x440.webp";
import image4 from "./sliderImages/supply-chain.avif";
import {
  BrainCircuit,
  Zap,
  ArrowRight,
  Headphones,
  FileText,
  Layers
} from "lucide-react";

const slides = [
  {
    id: 2,
    badge: "EMS",
    title: "EMS",
    heading: "End-to-End Electronic Manufacturing Solutions",
    description:
      "Bring your electronic products to life with Electrowiz's trusted manufacturing network. We collaborate with experienced EMS partners across India to offer PCB assembly, box-build assembly, testing, and volume production tailored to your requirements.",
    button: "Explore EMS Solutions",
    link: "/contact",
    image: image2,
    icon: Layers,
    highlights: [
      "PCB & Box-Build Assembly",
      "End-to-End Testing & QC",
      "High & Low Volume Production",
      "Trusted Local & Global Network"
    ]
  },
  {
    id: 3,
    badge: "EDM",
    title: "EDM",
    heading: "Innovative Electronic Design Services",
    description:
      "Transform your ideas into market-ready products through our network of experienced design partners. From concept development to PCB layout and embedded firmware, we help you find the right experts for every stage of product development.",
    button: "Explore EDM Solutions",
    link: "/contact",
    image: image3,
    icon: BrainCircuit,
    highlights: [
      "Concept to Prototype Design",
      "Custom PCB Layout & Schematics",
      "Embedded Firmware Engineering",
      "Hardware Architecture Planning"
    ]
  },
  {
    id: 4,
    badge: "Magnetics",
    title: "Magnetics",
    heading: "Custom Magnetics & Winding Solutions",
    description:
      "Electrowiz partners with specialized manufacturers to deliver custom magnetic components for power electronics, industrial automation, EV, telecom, and renewable energy applications. Whether you require prototypes or production quantities, we help source high-quality transformers, inductors, and coils designed to your specifications.",
    button: "Explore Magnetics Solutions",
    link: "/contact",
    image: image4,
    icon: Zap,
    highlights: [
      "Custom Transformer Design",
      "Inductor & Coil Sourcing",
      "Power Electronics Applications",
      "Prototype to Production Volume"
    ]
  }
];

const workflowSteps = [
  {
    step: "01",
    title: "Consultation & Requirement",
    description: "Submit your engineering specs or upload your BOM for automated analysis."
  },
  {
    step: "02",
    title: "Global Sourcing & Verification",
    description: "Our algorithm queries authorized global hubs for best pricing and guaranteed stock."
  },
  {
    step: "03",
    title: "Lab Inspection & Quality Control",
    description: "Components are inspected and verified against anti-counterfeit protocols."
  },
  {
    step: "04",
    title: "JIT Delivery & Lifecycle Support",
    description: "Receive tracked, secure delivery backed by ongoing technical assistance."
  }
];

const Service = () => {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  return (
    <div className={styles.container}>
      {/* HERO SECTION */}
      <section className={styles.heroSection}>
        <h1 className={styles.heroTitle}>Electrowiz Services</h1>
        <p className={styles.heroDescription}>
          Electrowiz simplifies the electronics supply chain by bringing together component sourcing, manufacturing, engineering services, and procurement under one roof. We help businesses source smarter, build faster, and scale confidently through our trusted global partner ecosystem.
        </p>


      </section>

      {/* INTERACTIVE ACCORDION / SHOWCASE SECTION */}
      <section className={styles.showcaseSection}>
        {/* ACCORDION SLIDER */}
        <div className={`${styles.sliderWrapper} ewKeepSection`}>
          <div className={styles.slider}>
            {slides.map((item, index) => {
              const Icon = item.icon;
              const isActive = active === index;
              return (
                <div
                  key={index}
                  className={`${styles.slide} ${isActive ? styles.active : ""}`}
                  style={{ backgroundImage: `url(${item.image})` }}
                  onClick={() => setActive(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setActive(index);
                  }}
                >
                  <div className={styles.slideOverlay} />
                  
                  {/* EXPANDED CONTENT */}
                  <div className={`${styles.content} ${isActive ? styles.show : ""}`}>
                    <div className={styles.cardBadge}>
                      <Icon size={16} />
                      <span>{item.badge}</span>
                    </div>
                    <h3>{item.heading}</h3>
                    <p>{item.description}</p>
                    
                    <button
                      className={styles.actionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(item.link);
                      }}
                    >
                      <span>{item.button}</span>
                      <ArrowRight size={16} />
                    </button>
                  </div>

                  {/* COLLAPSED / BOTTOM LABEL */}
                  <div className={styles.label}>
                    <div className={styles.labelHeader}>
                      <div className={styles.iconCircle}>
                        <Icon size={18} />
                      </div>
                      <div className={styles.titleStack}>
                        <span className={styles.slideTitleMain}>{item.title}</span>
                      </div>
                    </div>
                    <span className={styles.toggleIcon}>
                      {isActive ? "−" : "+"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </section>

     

      {/* CALL TO ACTION BANNER */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaBox}>
          <div className={styles.ctaContent}>
            <h2>Ready to Streamline Your Electronics Procurement & Systems?</h2>
            <p>
              Whether you need urgent component sourcing, custom BOM quoting, or enterprise computing solutions, 
              our team is standing by.
            </p>
            <div className={styles.ctaBtnGroup}>
              <button
                className={styles.primaryCta}
                onClick={() => navigate("/quoteRequest")}
              >
                <FileText size={18} />
                <span>Request Custom Quote</span>
              </button>
              <button
                className={styles.secondaryCta}
                onClick={() => navigate("/contact")}
              >
                <Headphones size={18} />
                <span>Contact Our Team</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Service;